import {
  DEFAULT_VALUES,
  DEPLOYMENT_KEY_FIELD,
  LAMBDA_ARCHITECTURES
} from "@/pages/DeploymentsPage/const"
import { getShellVariableLine } from "@/pages/DeploymentsPage/helpers/deployment-command"
import { getLambdaZipName } from "@/pages/DeploymentsPage/helpers/file"

import type { OSPlatform } from "@/icons"
import type { DeployCommandLine } from "@/pages/DeploymentsPage/helpers/deployment-command.types"

// Published layers are named kortex-<arch>; a previous Kodem layer is matched
// by this prefix when re-attaching.
const KODEM_LAYER_PREFIX = "kortex"

// The architecture options are Lambda platforms only, x86_64 when none matches
export const getLambdaArchitecture = (OSPlatform: OSPlatform) =>
  LAMBDA_ARCHITECTURES.find(({ platform }) => platform === OSPlatform) ??
  LAMBDA_ARCHITECTURES[0]

export const getPublishDefaultValues = () => ({
  key: Date.now().toString(),
  bucket: DEFAULT_VALUES.BUCKET
})

// Layer zips exceed the 50 MB direct upload limit, so they are staged in S3.
// ZIP is the file downloaded in the previous step.
export const getPublishLines = (
  version: string,
  { packageSpecArch, lambdaArch }: (typeof LAMBDA_ARCHITECTURES)[number]
) =>
  ({
    zip: { text: `ZIP=${getLambdaZipName(version, packageSpecArch)}` },
    bucket: getShellVariableLine("BUCKET", "bucket"),
    copy: { text: 'aws s3 cp "$ZIP" "s3://$BUCKET/kodem/$ZIP"' },
    publish: {
      text: `aws lambda publish-layer-version --layer-name ${KODEM_LAYER_PREFIX}-${packageSpecArch}`
    },
    content: { text: '--content "S3Bucket=$BUCKET,S3Key=kodem/$ZIP"' },
    arch: { text: `--compatible-architectures ${lambdaArch}` },
    query: { text: "--query LayerVersionArn --output text" }
  }) satisfies Record<string, DeployCommandLine>

export const PUBLISH_GROUPS: (keyof ReturnType<typeof getPublishLines>)[][] = [
  ["zip"],
  ["bucket"],
  ["copy"],
  ["publish", "content", "arch", "query"]
]

export const getConfigureDefaultValues = ({
  environment = DEFAULT_VALUES.ENVIRONMENT
}: { environment?: string } = {}) => ({
  key: Date.now().toString(),
  functionName: DEFAULT_VALUES.FUNCTION_NAME,
  layerArn: DEFAULT_VALUES.LAYER_ARN,
  deploymentKey: DEFAULT_VALUES.DEPLOYMENT_KEY,
  environment
})

// Keeps the function's other layers (dropping a previous Kodem layer) and
// environment variables: update-function-configuration replaces both.
// The jq programs are shell variables spanning two lines (jq ignores the
// newline), so every script line fits the command box.
export const CONFIGURE_LINES = {
  functionName: getShellVariableLine("FUNCTION_NAME", "functionName"),
  layerArn: getShellVariableLine("LAYER_ARN", "layerArn"),
  environment: getShellVariableLine("KODEM_ENVIRONMENT", "environment"),
  deploymentKey: getShellVariableLine(
    "KODEM_DEPLOYMENT_KEY",
    DEPLOYMENT_KEY_FIELD
  ),
  mergeComment: {
    text: "# Keep the function's current layers and environment variables",
    comment: true
  },
  get: { text: "aws lambda get-function-configuration" },
  // --output json: the user's AWS CLI default output may be text or table
  getArgs: {
    text: '--function-name "$FUNCTION_NAME" --output json > function.json'
  },
  layersFilter: { text: "JQ_LAYERS='.Layers[]?.Arn |" },
  layersFilterEnd: {
    text: `select(contains(":layer:${KODEM_LAYER_PREFIX}-") | not)'`
  },
  variablesFilter: {
    text: "JQ_VARIABLES='{Variables: ((.Environment.Variables // {}) +"
  },
  variablesFilterEnd: {
    text: "{KODEM_ENVIRONMENT: $env, KODEM_DEPLOYMENT_KEY: $key})}'"
  },
  jq: {
    text: 'jq --arg env "$KODEM_ENVIRONMENT" --arg key "$KODEM_DEPLOYMENT_KEY"'
  },
  jqFile: { text: '"$JQ_VARIABLES" function.json > environment.json' },
  update: { text: "aws lambda update-function-configuration" },
  updateFunction: { text: '--function-name "$FUNCTION_NAME"' },
  layers: {
    text: '--layers $(jq -r "$JQ_LAYERS" function.json) "$LAYER_ARN"'
  },
  variables: { text: "--environment file://environment.json" }
} satisfies Record<string, DeployCommandLine>

// A jq program's lines are groups of their own: a trailing "\" inside the
// quotes would reach jq
export const CONFIGURE_GROUPS: (keyof typeof CONFIGURE_LINES)[][] = [
  ["functionName"],
  ["layerArn"],
  ["environment"],
  ["deploymentKey"],
  ["mergeComment"],
  ["get", "getArgs"],
  ["layersFilter"],
  ["layersFilterEnd"],
  ["variablesFilter"],
  ["variablesFilterEnd"],
  ["jq", "jqFile"],
  ["update", "updateFunction", "layers", "variables"]
]
